import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Container, Row, Col, Button, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faHome, faMapLocationDot, faChartLine, faHandshake, faComments, faList, faMap, faMicrophone, faStop } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
// KOPS-384: February 16, 2026 - Interactive Seller CMA Chat Interface
// Built on HomePageV3.tsx foundation, adapted for seller CMA conversations
import SellerCMAConsultativeService from '../../services/seller-cma-chat.service';
import { useParams } from 'react-router-dom';
import InlinePropertyCards from '../../components/chat/InlinePropertyCards';
import CMAReportPanel from '../../components/seller-cma/CMAReportPanel';
import PropertyResultsPanel from '../../components/chat/PropertyResultsPanel';
import MobileChatLayout from '../../components/chat/MobileChatLayout';
// import SearchProgressPanel from '../../components/chat/SearchProgressPanel'; // KOPS-361: Replaced with RightPanelWithLeadCapture
import SaveSearchModal from '../../components/chat/SaveSearchModal';
import PropertyDetailsModal from '../customer-portal/PropertyDetailsModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import './styles/chat-realestate.css';
import './styles/chat-realestate-mobile.css';
import RightPanelWithLeadCapture from '../../components/chat/RightPanelWithLeadCapture';
import ContactAgentPanel from '../../components/chat/ContactAgentPanel';
import leadCaptureTracking from '../../services/lead-capture-tracking.service';
import ListMyHomeModal from '../../components/seller-cma/ListMyHomeModal';

// Message types
interface Message {
  id: string;
  type: 'user' | 'assistant' | 'listings' | 'system' | 'properties';
  content: string;
  listings?: any[];
  properties?: any[];
  // KOPS-343: Added suggested actions for consultative flow
  suggestedActions?: Array<{
    label: string;
    action: string;
  }>;
  timestamp: Date;
}

// Agent profile component
interface AgentProfile {
  name: string;
  photo: string;
  brokerage: string;
  phone: string;
  email: string;
  license: string;
}

// Minimal header with agent info (matches buyer portal HomePageV3 header exactly)
const MinimalHeader: React.FC<{ agent?: AgentProfile; setShowContactPanel?: (show: boolean) => void }> = ({ agent, setShowContactPanel }) => {
  const isAISearchPortal = window.location.hostname.includes('aisearchportal.com');

  return (
    <header className="minimal-header border-bottom" style={{ background: 'white', position: 'sticky', top: 0, zIndex: 1000 }}>
      <Container fluid className="py-2">
        <Row className="align-items-center">
          {agent && agent.brokerage && (
            <Col xs="auto" className="d-flex align-items-center me-3">
              {(agent as any).logo_url ? (
                <img
                  src={(agent as any).logo_url}
                  alt={agent.brokerage}
                  style={{ height: '40px', maxWidth: '160px', objectFit: 'contain' }}
                />
              ) : (
                <div>
                  <div className="fw-semibold" style={{ fontSize: '1rem' }}>{agent.brokerage}</div>
                </div>
              )}
            </Col>
          )}
          <Col>
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3">{isAISearchPortal ? 'AI Search Portal' : 'KairosIQ'}</h5>
              <Badge bg="primary" className="me-2">AI-Powered</Badge>
              <small className="text-muted">Seller Property Valuation Report</small>
            </div>
          </Col>
          {agent && agent.name !== 'Your Agent' && (
            <Col xs="auto" className="d-flex align-items-center">
              <img
                src={agent.photo}
                alt={agent.name}
                className="rounded-circle me-2"
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
              />
              <div className="me-3">
                <div className="fw-bold small">{agent.name}</div>
                {agent.brokerage && (
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>{agent.brokerage}</div>
                )}
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowContactPanel?.(true)}
              >
                Contact Agent
              </Button>
            </Col>
          )}
        </Row>
      </Container>
    </header>
  );
};

// Suggested prompts component
const SuggestedPrompts: React.FC<{ onSelectPrompt: (prompt: string) => void }> = ({ onSelectPrompt }) => {
  const prompts = [
    {
      icon: faHome,
      title: "First-time buyer",
      prompt: "I'm a first-time buyer looking for a starter home in Phoenix with good schools"
    },
    {
      icon: faMapLocationDot,
      title: "Relocating for work",
      prompt: "I'm relocating to Phoenix for work and need a place near downtown with a short commute"
    },
    {
      icon: faChartLine,
      title: "Investment property",
      prompt: "I'm interested in investment properties in Phoenix with good rental potential"
    },
    {
      icon: faHandshake,
      title: "Downsizing",
      prompt: "We're empty nesters looking to downsize to a smaller home with less maintenance"
    }
  ];

  return (
    <div className="suggested-prompts py-5">
      <h4 className="text-center mb-4">How can I help you find your next home?</h4>
      <Row className="g-3">
        {prompts.map((prompt, idx) => (
          <Col md={6} lg={3} key={idx}>
            <Card 
              className="h-100 cursor-pointer hover-shadow"
              onClick={() => onSelectPrompt(prompt.prompt)}
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={prompt.icon} size="2x" className="text-primary mb-3" />
                <h6>{prompt.title}</h6>
                <small className="text-muted">{prompt.prompt}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Listing card component for inline results - moved inside main component

// Message component - moved inside main component to access navigate and chatService

// Main chat interface component
interface SellerCMAProps {
  reportId?: string; // CMA Report ID from URL params
  onPropertiesFound?: (properties: any[], criteria: any) => void;
  showMapButton?: boolean;
  onViewMap?: () => void;
  initialQuery?: string;
}

const SellerCMAChat: React.FC<SellerCMAProps> = ({ 
  onPropertiesFound, 
  showMapButton = false, 
  onViewMap,
  initialQuery 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasEnoughContext, setHasEnoughContext] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [showProperties, setShowProperties] = useState(false);
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [showListMyHome, setShowListMyHome] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [panelViewMode, setPanelViewMode] = useState<'list' | 'map'>('list');
  const [searchCriteria, setSearchCriteria] = useState<any>({});
  const [searchProgress, setSearchProgress] = useState<{
    matchCount: number;
    searchQuality: 'initial' | 'too_broad' | 'refining' | 'optimal' | 'ready' | 'no_results';
  }>({
    matchCount: 0,
    searchQuality: 'initial'
  });
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [hasShownSaveSearchModal, setHasShownSaveSearchModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // KOPS-384: Seller CMA specific state
  const { reportId } = useParams<{ reportId: string }>();
  const [cmaData, setCMAData] = useState<any>(null);
  const [isLoadingCMA, setIsLoadingCMA] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // KOPS-384: Using seller-specific CMA consultative service
  const chatService = useRef(new SellerCMAConsultativeService());
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [mobileTab, setMobileTab] = useState<'chat' | 'list' | 'map'>('chat');
  const [savedPropertyIds, setSavedPropertyIds] = useState<Set<string>>(new Set());
  const [leadId, setLeadId] = useState<string | null>(null);
  
  // Agent profile - loaded dynamically from API
  const [agentProfile, setAgentProfile] = useState<AgentProfile>({
    name: "Your Agent",
    photo: "https://placehold.co/100x100/1e3a8a/ffffff?text=Agent",
    brokerage: "",
    phone: "",
    email: "",
    license: ""
  });

  // Load agent profile from URL param or report data
  useEffect(() => {
    const loadAgentProfile = async () => {
      try {
        // Check for agentId in URL params
        const agentId = searchParams.get('agentId');

        // Fetch agent profile from public API (no auth needed)
        const response = agentId
          ? await axiosInstance.get(`/public/agent-profile/${agentId}`)
          : await axiosInstance.get('/agent-profile');

        if (response.data?.success && response.data?.agent) {
          const agent = response.data.agent;
          const initials = (agent.name || 'Agent').split(' ').map((n: string) => n[0]).join('');
          setAgentProfile({
            name: agent.name || 'Your Agent',
            photo: agent.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name || 'Agent')}&background=1e3a8a&color=fff`,
            brokerage: agent.brokerage || '',
            phone: agent.phone || '',
            email: agent.email || '',
            license: agent.license || '',
            logo_url: agent.logo_url || null
          } as any);
          console.log('👤 [CMA Chat] Loaded agent profile:', agent.name);
        }
      } catch (error) {
        console.warn('⚠️ [CMA Chat] Could not load agent profile, using default');
      }
    };
    loadAgentProfile();
  }, [searchParams]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Restore leadId from localStorage on mount
  useEffect(() => {
    const savedLeadId = localStorage.getItem('chat_lead_id');
    if (savedLeadId) {
      setLeadId(savedLeadId);
    }
  }, []);

  // Initialize voice recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join(' ');
        if (inputRef.current) inputRef.current.value = transcript;
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
        if (inputValue.trim()) {
          handleSendMessage();
        }
      };

      recognitionInstance.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, []);

  // Initialize from URL on mount
  useEffect(() => {
    const query = searchParams.get('q');
    const criteria = searchParams.get('criteria');
    
    // Check for initial query from props (passed from homepage)
    if (initialQuery && !query) {
      if (inputRef.current) inputRef.current.value = initialQuery;
      setTimeout(() => {
        handleSendMessage(initialQuery);
      }, 100);
      return;
    }
    
    if (query) {
      // Restore search from URL
      if (inputRef.current) inputRef.current.value = query;
      setTimeout(() => {
        handleSendMessage(query);
      }, 100);
    }
    
    if (criteria) {
      try {
        const parsedCriteria = JSON.parse(criteria);
        setSearchCriteria(parsedCriteria);
      } catch (e) {
        console.error('Failed to parse criteria from URL:', e);
      }
    }
  }, [initialQuery]); // Add initialQuery to dependency array
  
  // KOPS-384: Load CMA data when reportId is available
  useEffect(() => {
    const loadCMAData = async () => {
      if (!reportId) {
        setIsLoadingCMA(false);
        return;
      }
      
      try {
        const response = await axiosInstance.get(`/public/cma-report/${reportId}`);
        console.log('📊 [CMA DATA] Loaded:', response.data);
        setCMAData(response.data);

        // Use agent info from CMA response if agent profile wasn't loaded from URL
        if (response.data.agent && agentProfile.name === 'Your Agent') {
          const a = response.data.agent;
          setAgentProfile({
            name: a.name || 'Your Agent',
            photo: a.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(a.name || 'Agent')}&background=1e3a8a&color=fff`,
            brokerage: a.brokerage || '',
            phone: a.phone || '',
            email: a.email || '',
            license: a.license || '',
            logo_url: a.logo || a.logo_url || null
          } as any);
          console.log('👤 [CMA Chat] Agent from CMA response:', a.name);
        }

        // Initialize chat with CMA data context
        // Check the actual response structure
        const report = response.data.report || response.data;
        
        if (report && report.property && report.valuation) {
          // Pass CMA data to the chat service
          chatService.current.setCMAData(report);
          
          const welcomeMessage: Message = {
            id: `msg_${Date.now()}`,
            type: 'assistant',
            content: `Hi! ${response.data.agent?.name || agentProfile.name || 'Your agent'} has prepared a market analysis for your property at ${report.property.address}. Based on ${report.comparables?.length || report.comparable_count || 0} recent comparable sales in your area, similar homes are selling in the $${Math.round((report.valuation.price_range?.min || report.valuation.estimated_value * 0.95) / 1000)}K-$${Math.round((report.valuation.price_range?.max || report.valuation.estimated_value * 1.05) / 1000)}K range. What questions do you have about your property's market value?`,
            timestamp: new Date()
          };
          setMessages([welcomeMessage]);
          console.log('💬 [CMA WELCOME] Set welcome message');
          
          // Set suggested questions for CMA context
          const suggestedActions = chatService.current.getSuggestedQuestions().map(q => ({
            text: q,
            prompt: q
          }));
          
          // You can set these as suggested actions if needed
          console.log('💡 [CMA SUGGESTIONS]', suggestedActions);
        } else {
          console.warn('⚠️ [CMA DATA] Unexpected structure:', {
            hasReport: !!report,
            hasProperty: !!report?.property,
            hasValuation: !!report?.valuation,
            keys: Object.keys(report || {})
          });
        }
      } catch (error) {
        console.error('Failed to load CMA data:', error);
        const errorMessage: Message = {
          id: `msg_${Date.now()}`,
          type: 'assistant', 
          content: 'Sorry, I couldn\'t load your CMA report. Please check the link and try again.',
          timestamp: new Date()
        };
        setMessages([errorMessage]);
      } finally {
        setIsLoadingCMA(false);
      }
    };
    
    loadCMAData();
  }, [reportId]);
  
  // Update URL when search criteria changes
  useEffect(() => {
    if (Object.keys(searchCriteria).length > 0) {
      const params = new URLSearchParams();
      
      // Build a human-readable query string
      const queryParts = [];
      if (searchCriteria.city) queryParts.push(searchCriteria.city);
      if (searchCriteria.beds) queryParts.push(`${searchCriteria.beds} bedroom`);
      if (searchCriteria.maxPrice) queryParts.push(`under ${searchCriteria.maxPrice}`);
      
      if (queryParts.length > 0) {
        params.set('q', queryParts.join(' '));
        params.set('criteria', JSON.stringify(searchCriteria));
        
        // Update URL without causing navigation
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [searchCriteria]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('q');
      const criteria = params.get('criteria');
      
      if (query) {
        if (inputRef.current) inputRef.current.value = query;
        // Clear current messages and start fresh
        setMessages([]);
        setProperties([]);
        setTimeout(() => {
          handleSendMessage(query);
        }, 100);
      } else {
        // No query in URL, reset to initial state
        setMessages([]);
        setProperties([]);
        setSearchCriteria({});
        setShowProperties(false);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle view all properties event
  useEffect(() => {
    const handleViewAll = (event: CustomEvent) => {
      const { properties } = event.detail;
      setProperties(properties);
      setShowProperties(true);
      setPanelViewMode('list'); // Start with list view
    };

    window.addEventListener('viewAllProperties', handleViewAll as any);
    return () => {
      window.removeEventListener('viewAllProperties', handleViewAll as any);
    };
  }, []);

  // Handle saving/unsaving a property
  const handleToggleSaveProperty = async (property: any) => {
    try {
      const sessionId = chatService.current?.getSessionId() || `chat_${Date.now()}`;
      const isSaved = savedPropertyIds.has(property.id);
      
      if (isSaved) {
        // Unsave property (not implemented in backend yet)
        setSavedPropertyIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(property.id);
          return newSet;
        });
      } else {
        // Save property
        const response = await axiosInstance.post('/public-buyer-profile/save-property', {
          sessionId,
          leadId,
          mlsNumber: property.mlsNumber || property.mls_number,
          propertyData: {
            address: property.address,
            city: property.city,
            state: property.state,
            zipCode: property.zipCode,
            price: property.price,
            bedrooms: property.bedrooms,
            bathrooms: property.bathrooms,
            squareFeet: property.squareFeet || property.square_feet,
            images: property.images
          }
        });
        
        if (response.data.success) {
          setSavedPropertyIds(prev => new Set(prev).add(property.id));
          
          // If this created a new lead, save the lead ID
          if (response.data.leadId && !leadId) {
            setLeadId(response.data.leadId);
          }
        }
      }
    } catch (error) {
      console.error('Error saving property:', error);
      // Could show a toast notification here
    }
  };

  // Handle email capture from right panel
  const handleEmailCapture = async (email: string) => {
    try {
      const sessionId = chatService.current?.getSessionId() || `chat_${Date.now()}`;
      
      // IMPORTANT: The backend will automatically detect white-label domains
      // from the x-portal-domain header and assign leads to the brokerage owner
      const response = await axiosInstance.post('/public-buyer-profile/create-profile', {
        sessionId,
        email,
        leadSource: 'right_panel',
        propertyPreferences: searchCriteria || {},
        conversationContext: {
          messageCount: messages.length,
          hasSearched: properties.length > 0,
          propertiesSaved: savedPropertyIds.size
        }
      });
      
      if (response.data.leadId) {
        setLeadId(response.data.leadId);
        leadCaptureTracking.captureEmail(email);
        
        // Track conversion event
        leadCaptureTracking.trackInteraction();
        
        console.log('✅ Email captured and lead created:', response.data.leadId);
      }
    } catch (error) {
      console.error('Failed to capture email:', error);
      throw error; // Let the component handle the error display
    }
  };

  // Handle voice input toggle
  const toggleVoiceInput = () => {
    if (!recognition) {
      alert('Voice recognition is not supported in your browser. Please use Chrome or Safari.');
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
      if (inputRef.current) inputRef.current.value = ''; // Clear input when starting voice
    }
  };

  // Handle sending a message
  const handleSendMessage = async (content?: string) => {
    const msg = content || inputRef.current?.value || '';
    if (!msg.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: msg,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    // Clear input (uncontrolled — use ref)
    if (inputRef.current) inputRef.current.value = '';
    setIsTyping(true);

    try {
      // Send to AI service with CMA context
      const response = await chatService.current.sendMessage(msg);
      
      // Update search progress if provided
      if (response.searchProgress) {
        setSearchProgress(response.searchProgress);
      }
      
      // Get suggested questions based on conversation stage
      const suggestedQuestions = chatService.current.getSuggestedQuestions();
      const suggestedActions = suggestedQuestions.map(q => ({
        label: q,
        action: q
      }));
      
      // Add assistant response with suggested actions
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.message || response.content || "I'm here to help you understand your property valuation. What would you like to know?",
        suggestedActions: suggestedActions, // Add suggested questions
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // If AI mentioned specific properties, show them after the message
      if (response.mentionedProperties && response.mentionedProperties.length > 0) {
        console.log('📍 [HOME] AI mentioned properties:', response.mentionedProperties);
        
        const formattedMentioned = response.mentionedProperties.map((prop: any) => ({
          id: prop.id,
          mlsNumber: prop.mlsNumber,
          address: prop.address,
          city: prop.city,
          state: prop.state,
          zipCode: prop.zipCode,
          price: prop.price,
          bedrooms: prop.beds || prop.bedrooms,
          bathrooms: prop.baths || prop.bathrooms,
          squareFeet: prop.sqft || prop.squareFeet,
          images: prop.images || [],
          latitude: prop.latitude,
          longitude: prop.longitude,
          features: prop.features || [],
          listingBrokerage: prop.listingBrokerage,
          listingAgent: prop.listingAgent,
          description: prop.description,
          // Add missing fields for AI analysis
          pool: prop.pool,
          yearBuilt: prop.yearBuilt,
          garage: prop.garage,
          hoaFee: prop.hoaFee,
          daysOnMarket: prop.daysOnMarket,
          propertyType: prop.propertyType,
          lotSize: prop.lotSize,
          fireplace: prop.fireplace,
          viewType: prop.viewType,
          // Map the correct field names
          beds: prop.beds || prop.bedrooms,
          baths: prop.baths || prop.bathrooms,
          sqft: prop.sqft || prop.squareFeet,
          // Enhanced fields for better AI responses
          subdivision: prop.subdivision,
          schoolDistrict: prop.schoolDistrict,
          priceReduction: prop.priceReduction,
          listingDate: prop.listingDate,
          modifiedDate: prop.modifiedDate,
          virtualTour: prop.virtualTour,
          spa: prop.spa,
          conditionInsights: prop.conditionInsights,
          mlsArea: prop.mlsArea,
          status: prop.status,
          // Parse features array if it exists
          allFeatures: prop.features || []
        }));
        
        const mentionedPropertiesMessage: Message = {
          id: (Date.now() + 1.5).toString(),
          type: 'properties',
          content: '',
          properties: formattedMentioned,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, mentionedPropertiesMessage]);
      }
      
      // Update context status (KOPS-343: now requires comprehensive context)
      // TODO: Fix getContext method in seller-cma-chat.service
      // const context = chatService.current.getContext();
      // const hasContext = !!(context.hasComprehensiveInfo || (context.extractedInfo?.city || context.extractedInfo?.zipCode));
      // setHasEnoughContext(hasContext);
      setHasEnoughContext(true); // Temporary fix for build
      
      // Update search criteria for the progress panel
      // if (context.extractedInfo) {
      //   setSearchCriteria(context.extractedInfo);
      // }
      
      // KOPS-343: Only show properties after meaningful conversation
      if (response.hasEnoughContext === false) {
        console.log('📝 [CHAT] Not enough context yet, continuing conversation...');
      }
      
      // If properties were returned, show them inline and in panel
      if (response.properties && response.properties.length > 0) {
        console.log('📍 [HOME] Properties received:', response.properties);
        
        const formattedProperties = response.properties.map((prop: any) => ({
          id: prop.id,
          mlsNumber: prop.mlsNumber,
          address: prop.address,
          city: prop.city,
          state: prop.state,
          zipCode: prop.zipCode,
          price: prop.price,
          bedrooms: prop.beds || prop.bedrooms,
          bathrooms: prop.baths || prop.bathrooms,
          squareFeet: prop.sqft || prop.squareFeet,
          images: prop.images || [],
          latitude: prop.latitude,
          longitude: prop.longitude,
          features: prop.features || [],
          listingBrokerage: prop.listingBrokerage,
          listingAgent: prop.listingAgent,
          description: prop.description,
          // Add missing fields for AI analysis
          pool: prop.pool,
          yearBuilt: prop.yearBuilt,
          garage: prop.garage,
          hoaFee: prop.hoaFee,
          daysOnMarket: prop.daysOnMarket,
          propertyType: prop.propertyType,
          lotSize: prop.lotSize,
          fireplace: prop.fireplace,
          viewType: prop.viewType,
          // Map the correct field names
          beds: prop.beds || prop.bedrooms,
          baths: prop.baths || prop.bathrooms,
          sqft: prop.sqft || prop.squareFeet,
          // Enhanced fields for better AI responses
          subdivision: prop.subdivision,
          schoolDistrict: prop.schoolDistrict,
          priceReduction: prop.priceReduction,
          listingDate: prop.listingDate,
          modifiedDate: prop.modifiedDate,
          virtualTour: prop.virtualTour,
          spa: prop.spa,
          conditionInsights: prop.conditionInsights,
          mlsArea: prop.mlsArea,
          status: prop.status,
          // Parse features array if it exists
          allFeatures: prop.features || []
        }));
        
        // Check if this is a new search or just returning existing properties
        const isNewSearch = properties.length === 0 || formattedProperties.length !== properties.length;
        
        // Update state for right panel
        setProperties(formattedProperties);
        // TODO: Fix this - SellerCMAConsultativeService doesn't have getContext method
        // const chatContext = chatService.current.getContext();
        // setSearchCriteria(chatContext.extractedInfo || {});
        setSearchCriteria({});
        
        // KOPS-361: Prompt for email/alerts before showing properties
        // Only show modal if we have properties, user hasn't provided email yet, and haven't shown modal before
        if (formattedProperties.length > 0 && !leadId && !hasShownSaveSearchModal) {
          setShowSaveSearchModal(true);
          setHasShownSaveSearchModal(true); // Mark that we've shown the modal
          // Properties will be shown after modal is handled
        } else {
          // User already has account, no properties found, or modal already shown
          setShowProperties(true);
        }
        
        // Call parent callback if provided
        if (onPropertiesFound) {
          onPropertiesFound(formattedProperties, {});
        }
        
        // Only add properties message for new searches, not for follow-up questions
        if (isNewSearch) {
          const propertiesMessage: Message = {
            id: (Date.now() + 2).toString(),
            type: 'properties',
            content: `Found ${formattedProperties.length} properties matching your criteria!`,
            properties: formattedProperties,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, propertiesMessage]);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "I apologize, but I'm having trouble connecting to our property search service. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSelectPrompt = (prompt: string) => {
    if (inputRef.current) inputRef.current.value = prompt;
    inputRef.current?.focus();
    handleSendMessage(prompt);
  };

  // Listing card component for inline results
  const ListingCard: React.FC<{ listing: any }> = ({ listing }) => {
    return (
      <Card className="mb-3 shadow-sm">
        <Row className="g-0">
          <Col md={4}>
            <img 
              src={listing.image} 
              className="img-fluid rounded-start h-100" 
              alt={listing.address}
              style={{ objectFit: 'cover' }}
            />
          </Col>
          <Col md={8}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h5 className="card-title">{listing.address}</h5>
                  <p className="h4 text-primary mb-2">{listing.price}</p>
                  <p className="text-muted mb-2">
                    {listing.beds} beds • {listing.baths} baths • {listing.sqft} sqft
                  </p>
                </div>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate(`/property/${listing.mlsNumber}`)}
                >
                  View Details
                </Button>
              </div>
              <div className="alert alert-light mt-3 mb-0">
                <small>
                  <strong>Why this matches:</strong> {listing.whyItMatches}
                </small>
              </div>
            </Card.Body>
          </Col>
        </Row>
      </Card>
    );
  };

  // Message component
  const ChatMessage: React.FC<{ message: Message }> = ({ message }) => {
    if (message.type === 'user') {
      return (
        <div className="d-flex justify-content-end mb-3">
          <div className="bg-primary text-white rounded-3 px-3 py-2" style={{ maxWidth: '70%' }}>
            {message.content}
          </div>
        </div>
      );
    }

    if (message.type === 'assistant') {
      return (
        <div className="d-flex flex-column align-items-start mb-3">
          <div className="bg-light rounded-3 px-3 py-2 mb-2" style={{ maxWidth: '70%' }}>
            {message.content}
          </div>
          {/* KOPS-343: Suggested actions for guided conversation */}
          {message.suggestedActions && message.suggestedActions.length > 0 && (
            <div className="d-flex flex-wrap gap-2 ms-2" style={{ maxWidth: '70%' }}>
              {message.suggestedActions.map((action, idx) => (
                <Button
                  key={idx}
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleSelectPrompt(action.label)}
                  className="rounded-pill"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (message.type === 'properties' && message.properties) {
      return (
        <div className="properties-results my-3">
          <InlinePropertyCards 
            properties={message.properties}
            maxDisplay={3}
            onViewAll={() => {
              // This will be handled by the parent component
              const event = new CustomEvent('viewAllProperties', { 
                detail: { properties: message.properties } 
              });
              window.dispatchEvent(event);
            }}
            onPropertyClick={(property) => {
              setSelectedProperty(property);
              setShowPropertyModal(true);
            }}
          />
        </div>
      );
    }

    if (message.type === 'listings' && message.listings) {
      return (
        <div className="listings-results my-4">
          <h5 className="mb-3">Here are homes that match what you're looking for:</h5>
          {message.listings.map((listing, idx) => (
            <ListingCard key={idx} listing={listing} />
          ))}
          <div className="text-center mt-3">
            <Button 
              variant="outline-primary" 
              className="me-2"
              onClick={() => {
                // TODO: Fix this - SellerCMAConsultativeService doesn't have getContext method
                navigate('/search', { state: { criteria: searchCriteria } });
              }}
            >
              Show more results
            </Button>
            <Button 
              variant="primary"
              onClick={() => {
                // TODO: Fix this - SellerCMAConsultativeService doesn't have getContext method
                navigate('/search?view=map', { state: { criteria: searchCriteria } });
              }}
            >
              View on map
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="chat-interface-v3" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <MinimalHeader agent={agentProfile} setShowContactPanel={setShowContactPanel} />

      <Container fluid className="flex-grow-1 d-flex" style={{ height: 'calc(100vh - 60px)', minHeight: 0, overflow: 'hidden' }}>
        <Row className="w-100 g-0" style={{ height: '100%' }}>
          {/* Main chat area */}
          <Col
            lg={8}
            className={`d-flex flex-column ${isMobile && showProperties && mobileTab !== 'chat' ? 'd-none' : ''}`}
            style={{ height: '100%', minHeight: 0 }}>
            {/* Messages area */}
            <div className="flex-grow-1 overflow-auto p-4" style={{ background: '#f8f9fa', minHeight: 0 }}>
              <Container style={{ maxWidth: '800px' }}>
                {/* Show loading state if we have a reportId and are loading */}
                {reportId && isLoadingCMA && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading your CMA report...</p>
                  </div>
                )}
                
                {/* Show welcome/prompts only if no reportId and no messages */}
                {!reportId && messages.length === 0 && (
                  <>
                    <div className="text-center py-5">
                      <h2 className="mb-3">Welcome to chat.realestate</h2>
                      <p className="lead text-muted">
                        Tell me about your ideal home and I'll help you find properties that match your needs.
                      </p>
                    </div>
                    <SuggestedPrompts onSelectPrompt={handleSelectPrompt} />
                  </>
                )}

                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}

                {isTyping && (
                  <div className="d-flex justify-content-start mb-3">
                    <div className="bg-light rounded-3 px-3 py-2">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Context indicator */}
                {messages.length > 0 && !hasEnoughContext && (
                  <div className="text-center text-muted small mb-3">
                    <em>Tell me more about your location and preferences to see matching properties...</em>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </Container>
            </div>

            {/* Input area */}
            <div className="border-top p-3" style={{ background: 'white' }}>
              <Container style={{ maxWidth: '800px' }}>
                <form onSubmit={(e) => { e.preventDefault(); const val = inputRef.current?.value || ''; if (val.trim()) handleSendMessage(val); }}>
                  <div className="input-group">
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control form-control-lg"
                      placeholder={isListening ? "Listening... speak now" : "Describe what you're looking for in a home..."}
                      defaultValue={inputValue}
                      style={{ borderRight: 'none' }}
                    />
                    <Button 
                      type="button"
                      variant={isListening ? "danger" : "outline-secondary"}
                      onClick={toggleVoiceInput}
                      style={{ borderLeft: 'none', borderRight: 'none' }}
                      title={isListening ? "Stop recording" : "Use voice search"}
                    >
                      <FontAwesomeIcon icon={isListening ? faStop : faMicrophone} />
                    </Button>
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={false}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </Button>
                  </div>
                  <small className="text-muted mt-1 d-block">
                    No sign-up required. Your conversation helps us understand your needs.
                  </small>
                </form>
              </Container>
            </div>
          </Col>

          {/* Side panel - transforms from info to properties */}
          <Col 
            lg={4} 
            className={`border-start ${isMobile ? (showProperties && mobileTab !== 'chat' ? 'mobile-property-panel active' : 'd-none') : 'd-none d-lg-block'}`} 
            style={{ 
              background: showProperties ? 'white' : '#f8f9fa', 
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease' 
            }}>
            {showContactPanel ? (
              <ContactAgentPanel
                agent={agentProfile}
                leadId={leadId}
                agentId={(agentProfile as any).userId || new URLSearchParams(window.location.search).get('agentId') || ''}
                context="seller"
                propertyAddress={cmaData?.report?.property?.address}
                onClose={() => setShowContactPanel(false)}
              />
            ) : !showProperties ? (
              // Show CMA data if available, otherwise show lead capture
              cmaData && cmaData.report ? (
                // Enhanced CMA Report Panel with Map
                <CMAReportPanel cmaData={cmaData} />
              ) : messages.length === 0 ? (
                // Information panel replaced by RightPanelWithLeadCapture
                <RightPanelWithLeadCapture
                  sessionId={chatService.current?.getSessionId() || ''}
                  onEmailCapture={handleEmailCapture}
                  hasMessages={false}
                />
              ) : (
                // When messages exist, show RightPanelWithLeadCapture with active messaging
                <RightPanelWithLeadCapture
                  sessionId={chatService.current?.getSessionId() || ''}
                  onEmailCapture={handleEmailCapture}
                  hasMessages={true}
                />
              )) : (
              // Properties panel with list/map toggle
              <PropertyResultsPanel
                properties={properties}
                viewMode={panelViewMode}
                onViewModeChange={setPanelViewMode}
                onPropertySelect={(property) => {
                  setSelectedProperty(property);
                  setShowPropertyModal(true);
                }}
                searchCriteria={searchCriteria}
                onUpdateFilters={(filters) => {
                  // Update search criteria and trigger new search
                  setSearchCriteria({ ...searchCriteria, ...filters });
                  const newMessage = `Show me properties ${filters.maxPrice ? `under $${filters.maxPrice.toLocaleString()}` : ''} ${filters.bedrooms ? `with ${filters.bedrooms}+ bedrooms` : ''}`;
                  handleSendMessage(newMessage.trim());
                }}
                savedPropertyIds={savedPropertyIds}
                onToggleSave={handleToggleSaveProperty}
                showMapButton={showMapButton}
                onViewMap={onViewMap}
              />
            )}
          </Col>
        </Row>
      </Container>
      
      {/* Mobile Tab Bar */}
      {isMobile && showProperties && (
        <div className="mobile-tab-bar">
          <button
            className={`mobile-tab ${mobileTab === 'chat' ? 'active' : ''}`}
            onClick={() => setMobileTab('chat')}
          >
            <FontAwesomeIcon icon={faComments} className="mobile-tab-icon" />
            <span className="mobile-tab-label">Chat</span>
          </button>
          
          <button
            className={`mobile-tab ${mobileTab === 'list' ? 'active' : ''}`}
            onClick={() => {
              setMobileTab('list');
              setPanelViewMode('list');
            }}
          >
            <FontAwesomeIcon icon={faList} className="mobile-tab-icon" />
            <span className="mobile-tab-label">List</span>
            <span className="mobile-tab-badge">{properties.length}</span>
          </button>
          
          <button
            className={`mobile-tab ${mobileTab === 'map' ? 'active' : ''}`}
            onClick={() => {
              setMobileTab('map');
              setPanelViewMode('map');
            }}
          >
            <FontAwesomeIcon icon={faMap} className="mobile-tab-icon" />
            <span className="mobile-tab-label">Map</span>
          </button>
        </div>
      )}
      
      {/* Save Search Modal */}
      <SaveSearchModal
        open={showSaveSearchModal}
        onClose={() => setShowSaveSearchModal(false)}
        onSave={async (data) => {
          console.log('💾 [SAVE SEARCH] User data:', data);
          
          try {
            // Create buyer profile using the public endpoint
            const sessionId = chatService.current?.getSessionId() || `chat_${Date.now()}`;
            
            const response = await axiosInstance.post('/public-buyer-profile/create-profile', {
              sessionId,
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              phone: data.phone,
              propertyPreferences: {
                bedrooms: searchCriteria.beds,
                bathrooms: searchCriteria.baths,
                property_type: searchCriteria.propertyType || 'Single Family',
                budget_min: searchCriteria.minPrice,
                budget_max: searchCriteria.maxPrice,
                features: searchCriteria.features
              },
              locationPreferences: {
                preferred_cities: searchCriteria.city ? [searchCriteria.city] : [],
                preferred_state: searchCriteria.state || 'Arizona'
              },
              buyingTimeframe: searchCriteria.timeline || 'Just Looking'
            });

            if (response.data.success) {
              // Save the lead ID for future property saves
              if (response.data.leadId) {
                setLeadId(response.data.leadId);
              }
              
              // Subscribe to alerts based on user preference
              if (data.agreeToTerms && data.alertFrequency) {
                await axiosInstance.post('/public-buyer-profile/subscribe-alerts', {
                  sessionId,
                  email: data.email,
                  searchCriteria: searchCriteria,
                  frequency: data.alertFrequency
                });
              }
              
              // Store lead ID for future actions
              localStorage.setItem('chat_lead_id', response.data.leadId);
              
              // Show success message
              const successMessage: Message = {
                id: Date.now().toString(),
                type: 'system',
                content: `✅ Great! I've saved your search and will email you at ${data.email} when new properties match. Here are your current matches:`,
                timestamp: new Date()
              };
              setMessages(prev => [...prev, successMessage]);
            }
          } catch (error) {
            console.error('Error saving search:', error);
            // Show error message
            const errorMessage: Message = {
              id: Date.now().toString(),
              type: 'system',
              content: `⚠️ There was an issue saving your search. You can still view the properties below:`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
          }
          
          // Show the properties regardless
          setShowSaveSearchModal(false);
          setShowProperties(true);
        }}
        searchCriteria={searchCriteria}
        matchCount={properties.length || searchProgress.matchCount}
      />
      
      {/* Property Details Modal */}
      <PropertyDetailsModal
        show={showPropertyModal}
        property={selectedProperty}
        onHide={() => setShowPropertyModal(false)}
        onSave={(property) => {
          console.log('Property saved:', property);
          // Handle save functionality if needed
        }}
        isSaved={false}
      />

      {/* Fair Housing Footer - Arizona Compliance */}
      <footer className="border-top mt-auto py-3" style={{ background: '#f8f9fa' }}>
        <Container fluid>
          <Row className="align-items-center justify-content-center text-center">
            <Col xs="auto">
              <img
                src={require('../../assets/img/logos/equal-housing-opportunity-logo-1200w.jpg')}
                alt="Equal Housing Opportunity"
                style={{ height: '30px', objectFit: 'contain' }}
              />
            </Col>
            <Col xs="auto">
              <small className="text-muted">
                Equal Housing Opportunity. All information deemed reliable but not guaranteed.
              </small>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* KOPS-518: Floating "List My Home" button — always visible */}
      <div style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999
      }}>
        <Button
          onClick={() => setShowListMyHome(true)}
          style={{
            background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)',
            border: 'none',
            color: '#0F1E35',
            fontWeight: 700,
            fontSize: '16px',
            padding: '16px 32px',
            borderRadius: '50px',
            boxShadow: '0 8px 32px rgba(201,149,42,0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '20px' }}>📸</span> List My Home
        </Button>
      </div>

      {/* KOPS-518: List My Home modal */}
      <ListMyHomeModal
        show={showListMyHome}
        onHide={() => setShowListMyHome(false)}
        leadId={(() => {
          try {
            const stored = sessionStorage.getItem('publicValuation');
            return stored ? JSON.parse(stored).leadId : '';
          } catch { return ''; }
        })()}
        address={(() => {
          try {
            const stored = sessionStorage.getItem('publicValuation');
            return stored ? JSON.parse(stored).address : cmaData?.report?.propertyData?.unparsed_address || '';
          } catch { return ''; }
        })()}
        estimatedValue={(() => {
          try {
            const stored = sessionStorage.getItem('publicValuation');
            if (stored) return JSON.parse(stored).valuationSummary?.predicted_value;
            return cmaData?.report?.predictionData?.predicted_value;
          } catch { return undefined; }
        })()}
      />
    </div>
  );
};

export default SellerCMAChat;